import { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import ChartCard, { ShowAllChip } from "./ChartCard";
import { COLORS, MOTION_MS, SERIES, fmtCount, reducedMotion } from "./emsTheme";

const MAX_PARTS = 5;
const shareText = (n, total) => {
    if (!total || !n) return "0%";
    const p = (n * 100) / total;
    return p < 1 ? "<1%" : `${Math.round(p)}%`;
};

/**
 * One 100% bar split into its parts, with a ranked list under it — for breakdowns with a few
 * parts, where a donut would be one more ring that looks like the others. Top five in the series
 * colours, the rest as Others. Clicking a part or row calls onSelect with that row.
 * rows: [{ key, label, value }]
 */
const StackedShareCard = ({ eyebrow, title, info, rows, unitLabel = "tickets", onSelect, cardState, fileName, focused }) => {
    const [hover, setHover] = useState(null);
    const motion = !reducedMotion();
    // Only when the rows change, not on every hover.
    const { sorted, total, parts } = useMemo(() => {
        const sortedRows = [...(rows || [])].filter((r) => r.value > 0).sort((a, b) => b.value - a.value);
        const top = sortedRows.slice(0, MAX_PARTS);
        const rest = sortedRows.slice(MAX_PARTS);
        return {
            sorted: sortedRows,
            total: sortedRows.reduce((a, r) => a + r.value, 0),
            parts: rest.length ? [...top, { key: "__others", label: `Others (${rest.length})`, value: rest.reduce((a, r) => a + r.value, 0), others: true }] : top,
        };
    }, [rows]);
    const colorOf = (p, i) => (p.others ? COLORS.context : SERIES[i % SERIES.length]);
    const clickable = (p) => onSelect && !p.others;

    return (
        <ChartCard {...cardState} eyebrow={eyebrow} title={title} info={info} empty={cardState.empty || (!cardState.loading && !cardState.error && !total)}
            fileName={fileName} height={200} ariaSummary={title}
            actions={focused ? <ShowAllChip label={focused.label} onClear={focused.onClear} /> : undefined}
            table={{ columns: [{ key: "label", label: eyebrow }, { key: "value", label: unitLabel, align: "right", text: (r) => fmtCount(r.value) }, { key: "share", label: "Share", align: "right", text: (r) => shareText(r.value, total) }], rows: sorted }}>
            <Box sx={{ display: "grid", gap: 2 }}>
                <Box sx={{ display: "flex", height: 18, borderRadius: "9px", overflow: "hidden", gap: "2px", bgcolor: "#F3F1F7" }}>
                    {parts.map((p, i) => (
                        <Box key={p.key ?? p.label} title={`${p.label}: ${fmtCount(p.value)} (${shareText(p.value, total)})`}
                            onClick={clickable(p) ? () => onSelect(p) : undefined} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
                            sx={{ flexGrow: p.value, flexBasis: 0, minWidth: 6, bgcolor: colorOf(p, i), cursor: clickable(p) ? "pointer" : "default",
                                opacity: hover == null || hover === i ? 1 : 0.4, transition: motion ? `opacity 150ms, flex-grow ${MOTION_MS}ms` : "none" }} />
                    ))}
                </Box>
                <Box component="ul" aria-label={`${title}, parts`} sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: 0.25 }}>
                    {parts.map((p, i) => {
                        const can = clickable(p);
                        return (
                            <Box component="li" key={p.key ?? p.label}>
                                <Box component={can ? "button" : "div"} type={can ? "button" : undefined} onClick={can ? () => onSelect(p) : undefined}
                                    onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} onFocus={() => setHover(i)} onBlur={() => setHover(null)}
                                    aria-label={`${p.label}: ${fmtCount(p.value)} ${unitLabel}, ${shareText(p.value, total)}${can ? ". Focus the page on this" : ""}`}
                                    sx={{ all: "unset", boxSizing: "border-box", width: "100%", display: "grid", gridTemplateColumns: { xs: "10px minmax(0, 1fr) auto 40px", sm: "10px minmax(0, 1fr) minmax(48px, 30%) auto 40px" },
                                        alignItems: "center", gap: 1.25, px: 1, py: 0.75, borderRadius: "8px", cursor: can ? "pointer" : "default",
                                        bgcolor: hover === i ? COLORS.ground : "transparent", transition: "background-color 150ms",
                                        "&:focus-visible": { outline: `2px solid ${COLORS.purple}` } }}>
                                    <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: colorOf(p, i) }} />
                                    <Typography title={p.label} sx={{ fontSize: 13, color: COLORS.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.label}</Typography>
                                    <Box sx={{ display: { xs: "none", sm: "block" }, height: 6, borderRadius: 3, bgcolor: "#F3F1F7", overflow: "hidden" }} aria-hidden>
                                        <Box sx={{ height: "100%", width: `${total ? (p.value * 100) / total : 0}%`, bgcolor: colorOf(p, i), borderRadius: 3 }} />
                                    </Box>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: COLORS.ink, fontVariantNumeric: "tabular-nums", textAlign: "right" }}>{fmtCount(p.value)}</Typography>
                                    <Typography sx={{ fontSize: 12, color: COLORS.ink3, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{shareText(p.value, total)}</Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </ChartCard>
    );
};

export default StackedShareCard;
